import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { DeckPrint } from "@/components/deck/DeckPrint";
import { DeckRoot } from "@/components/deck/DeckRoot";
import { getDeck } from "@/decks/registry";
import { useDeckConfig } from "@/hooks/useDeckConfig";
import { isEmbeddedPreview } from "@/hooks/usePreviewTextEditing";
import { I18nProvider, isLocale, type Locale } from "@/i18n";

// Chunk lazy separado: el admin y sus deps (Supabase, dnd-kit, React Query) nunca entran al
// bundle público. Solo se carga al navegar a /admin/*.
const AdminApp = lazy(() => import("@/admin/AdminApp"));
// Vista de presentador: chunk aparte con el login; pide las notas autenticada.
const PresenterApp = lazy(() => import("@/presenter/PresenterApp"));

// Origen del backoffice. Si está definido, el admin vive allá (/decks) con su propio login
// y /admin/* solo redirige; sin definir (dev local) se usa el admin embebido de este repo.
const BACKOFFICE_URL = (import.meta.env.VITE_BACKOFFICE_URL ?? "").replace(
  /\/+$/,
  "",
);

// /admin -> {backoffice}/decks y /admin/decks/:id -> {backoffice}/decks/:id.
function AdminRedirect() {
  const { pathname, search } = useLocation();
  const rest = pathname.replace(/^\/admin\/?/, "").replace(/^decks\/?/, "");
  const target = `${BACKOFFICE_URL}/decks${rest ? `/${rest}` : ""}${search}`;

  useEffect(() => {
    window.location.replace(target);
  }, [target]);

  return <div className="min-h-screen bg-neutral-100" />;
}

function NotFound() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-landing-black px-6 text-center text-white">
      <div>
        <p className="font-mono text-sm text-landing-accent">404</p>
        <h1 className="mt-2 text-2xl font-semibold">Deck no encontrado</h1>
      </div>
    </main>
  );
}

// Resuelve la config publicada en runtime y renderiza el deck (o su versión de impresión)
// con los overrides/alias de i18n aplicados. Mientras carga (sin warm cache) muestra el
// fondo del deck para evitar flash; ante fallo del fetch usa el deck compilado.
function ResolvedDeck({
  deckId,
  locale,
  print,
}: {
  deckId: string;
  locale: Locale;
  print: boolean;
}) {
  // `?preview=<token>` (usado por el iframe del editor) sirve el borrador en vez del publicado.
  const [searchParams] = useSearchParams();
  const preview = searchParams.get("preview") ?? undefined;
  const { resolution, loading } = useDeckConfig(deckId, preview);

  if (!resolution) {
    if (loading) {
      return <div className="fixed inset-0 bg-landing-black" />;
    }
    return <NotFound />;
  }

  return (
    <I18nProvider
      locale={locale}
      overrides={resolution.overrides}
      aliases={resolution.aliases}
      traceMessages={!print && isEmbeddedPreview(preview)}
    >
      {print ? (
        <DeckPrint deck={resolution.deck} />
      ) : (
        <DeckRoot deck={resolution.deck} />
      )}
    </I18nProvider>
  );
}

// Ruta de decks runtime (duplicados no compilados): el gate y la redirección de idioma
// dependen de la config publicada (fetch), porque el id no existe en el registry pero su
// component set sí. Mantiene el manejo de `?preview=<token>` y preserva print + query.
function RuntimeDeckRoute({
  deckId,
  locale,
  print,
}: {
  deckId: string;
  locale: Locale;
  print: boolean;
}) {
  const [searchParams] = useSearchParams();
  const preview = searchParams.get("preview") ?? undefined;
  const { resolution, loading } = useDeckConfig(deckId, preview);

  if (!resolution) {
    if (loading) {
      return <div className="fixed inset-0 bg-landing-black" />;
    }
    return <NotFound />;
  }

  // El idioma disponible viene del deck resuelto (meta.languages), no del compilado.
  if (!resolution.languages.includes(locale)) {
    const target = resolution.languages[0];
    if (!target) return <NotFound />;
    const search = searchParams.toString();
    const qs = search ? `?${search}` : "";
    const path = print
      ? `/print/${target}/${deckId}${qs}`
      : `/${target}/${deckId}${qs}`;
    return <Navigate to={path} replace />;
  }

  return (
    <I18nProvider
      locale={locale}
      overrides={resolution.overrides}
      aliases={resolution.aliases}
      traceMessages={!print && isEmbeddedPreview(preview)}
    >
      {print ? (
        <DeckPrint deck={resolution.deck} />
      ) : (
        <DeckRoot deck={resolution.deck} />
      )}
    </I18nProvider>
  );
}

function DeckRoute({ print }: { print: boolean }) {
  const { locale: rawLocale, deckId } = useParams();
  const locale: Locale | null = isLocale(rawLocale) ? rawLocale : null;

  useEffect(() => {
    if (locale) {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  if (!locale || !deckId) {
    return <NotFound />;
  }

  // Decks compilados (base): gate y redirección de idioma síncronos sobre el registry,
  // sin depender del fetch de config. Se preserva el comportamiento previo tal cual.
  const compiled = getDeck(deckId);
  if (compiled) {
    // Si el deck no está disponible en este idioma, redirige a su primer idioma
    // disponible (p. ej. /en/prospeccion -> /es/prospeccion), preservando print.
    if (!compiled.languages.includes(locale)) {
      const target = compiled.languages[0];
      const path = print
        ? `/print/${target}/${deckId}`
        : `/${target}/${deckId}`;
      return <Navigate to={path} replace />;
    }
    return <ResolvedDeck deckId={deckId} locale={locale} print={print} />;
  }

  // Deck no compilado (duplicado runtime): se resuelve vía config publicada.
  return <RuntimeDeckRoute deckId={deckId} locale={locale} print={print} />;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/admin/*"
          element={
            BACKOFFICE_URL ? (
              <AdminRedirect />
            ) : (
              <Suspense
                fallback={<div className="min-h-screen bg-neutral-100" />}
              >
                <AdminApp />
              </Suspense>
            )
          }
        />
        <Route
          path="/presentador/:locale/:deckId"
          element={
            <Suspense fallback={<div className="min-h-screen bg-neutral-950" />}>
              <PresenterApp />
            </Suspense>
          }
        />
        <Route path="/:locale/:deckId" element={<DeckRoute print={false} />} />
        <Route
          path="/:locale/:deckId/print"
          element={<DeckRoute print />}
        />
        <Route path="/print/:locale/:deckId" element={<DeckRoute print />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
